// ===================================================================
// 📢 IMPUGNAÇÕES AO EDITAL — ADMIN
// ===================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { styles } from '../../styles/globalStyles';
import { Alert } from '../../components/ui/Alert';
import { Modal } from '../../components/ui/Modal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { apiClient } from '../../api/apiClient';
import { formatDate } from '../../utils/helpers';
import { useNotifications } from '../../contexts/NotificationContext';
import { useAdminProcess } from '../../contexts/AdminProcessContext';
import { getButtonStyle, getStatusBadgeStyle } from '../../constants/statusTranslations';

export const EditalChallengeManager = React.memo(function EditalChallengeManager() {
  const { selectedProcessId, setSelectedProcessId, processes } = useAdminProcess();
  const { addNotification } = useNotifications();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selected, setSelected] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [decision, setDecision] = useState('deferred');
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');

  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const params = {};
      if (selectedProcessId) params.process_id = selectedProcessId;
      if (filterStatus) params.status = filterStatus;
      const { data, error: err } = await apiClient.listEditalChallenges(params);
      if (err) throw new Error(err);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'Erro ao carregar impugnações');
      setItems([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [selectedProcessId, filterStatus]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openRespond = (row) => {
    setSelected(row);
    setResponseText(row.admin_response || '');
    setDecision(row.status === 'rejected' ? 'rejected' : 'deferred');
    setModalError('');
  };

  const handleRespond = async () => {
    if (!selected) return;
    setModalError('');
    if ((responseText || '').trim().length < 5) {
      setModalError('Informe a resposta (mínimo 5 caracteres).');
      return;
    }
    setSaving(true);
    try {
      const { data, error: err } = await apiClient.respondEditalChallenge(selected.id, {
        status: decision,
        admin_response: responseText.trim(),
      });
      if (err) throw new Error(err);
      addNotification('Sucesso', `Impugnação ${data?.protocol || ''} respondida.`, 'success');
      setSelected(null);
      await loadData(true);
    } catch (e) {
      setModalError(e.message || 'Erro ao responder');
    } finally {
      setSaving(false);
    }
  };

  const statusBadge = useMemo(() => ({
    pending: getStatusBadgeStyle('pending') || { backgroundColor: '#FEF3C7', color: '#92400E' },
    deferred: getStatusBadgeStyle('approved') || { backgroundColor: '#D1FAE5', color: '#065F46' },
    rejected: getStatusBadgeStyle('rejected') || { backgroundColor: '#FEE2E2', color: '#991B1B' },
  }), []);

  if (loading) return <LoadingSpinner text="Carregando impugnações..." />;

  return (
    <div style={styles.container}>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ margin: 0, color: '#111827' }}>📢 Impugnações ao edital</h2>
        <p style={{ margin: '8px 0 0', color: '#6B7280', fontSize: '14px' }}>
          Recebidas pelo formulário público (sem login). Responda com deferimento ou indeferimento.
        </p>
      </div>

      {error && <Alert message={error} type="error" onClose={() => setError('')} />}

      <div style={{ ...styles.card, marginBottom: '16px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
          <div>
            <label style={styles.label}>Processo</label>
            <select
              value={selectedProcessId || ''}
              onChange={(e) => setSelectedProcessId(e.target.value ? Number(e.target.value) : null)}
              style={styles.input}
            >
              <option value="">Todos</option>
              {(processes || []).map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={styles.label}>Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={styles.input}
            >
              <option value="">Todos</option>
              <option value="pending">Pendente</option>
              <option value="deferred">Deferida</option>
              <option value="rejected">Indeferida</option>
            </select>
          </div>
          <button type="button" onClick={() => loadData()} style={getButtonStyle('secondary', 'small')}>
            Atualizar
          </button>
        </div>
      </div>

      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Registros ({items.length})</h3>
        {items.length === 0 ? (
          <p style={{ color: '#6B7280' }}>Nenhuma impugnação encontrada.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.tableHeader}>Protocolo</th>
                  <th style={styles.tableHeader}>Processo</th>
                  <th style={styles.tableHeader}>Interessado</th>
                  <th style={styles.tableHeader}>Data</th>
                  <th style={styles.tableHeader}>Status</th>
                  <th style={styles.tableHeader}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id}>
                    <td style={styles.tableCell}><strong>{row.protocol}</strong></td>
                    <td style={styles.tableCell}>{row.process_name}</td>
                    <td style={styles.tableCell}>
                      <div>{row.submitter_name}</div>
                      <div style={{ fontSize: '12px', color: '#6B7280' }}>{row.submitter_email}</div>
                    </td>
                    <td style={styles.tableCell}>{formatDate(row.created_at)}</td>
                    <td style={styles.tableCell}>
                      <span style={{ ...statusBadge[row.status] || {}, padding: '4px 8px', borderRadius: '999px', fontSize: '12px' }}>
                        {row.status_label || row.status}
                      </span>
                    </td>
                    <td style={styles.tableCell}>
                      <button
                        type="button"
                        onClick={() => openRespond(row)}
                        style={getButtonStyle(row.status === 'pending' ? 'primary' : 'secondary', 'small')}
                      >
                        {row.status === 'pending' ? 'Analisar' : 'Ver / Editar'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        isOpen={!!selected}
        onClose={() => !saving && setSelected(null)}
        title={selected ? `Impugnação ${selected.protocol}` : 'Impugnação'}
      >
        {selected && (
          <div>
            {modalError && (
              <Alert message={modalError} type="error" onClose={() => setModalError('')} />
            )}
            <p style={{ marginBottom: '8px' }}><strong>Processo:</strong> {selected.process_name}</p>
            <p style={{ marginBottom: '8px' }}><strong>Interessado:</strong> {selected.submitter_name}</p>
            <p style={{ marginBottom: '8px' }}><strong>E-mail:</strong> {selected.submitter_email}</p>
            {selected.submitter_cpf && <p style={{ marginBottom: '8px' }}><strong>CPF:</strong> {selected.submitter_cpf}</p>}
            {selected.submitter_phone && <p style={{ marginBottom: '8px' }}><strong>Telefone:</strong> {selected.submitter_phone}</p>}
            <p style={{ marginBottom: '8px' }}><strong>Justificativa:</strong></p>
            <div style={{
              whiteSpace: 'pre-wrap',
              background: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '12px',
              maxHeight: '200px',
              overflowY: 'auto',
            }}>
              {selected.justification}
            </div>
            {(selected.documents || []).length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <strong>Anexos:</strong>
                <ul style={{ margin: '6px 0 0', paddingLeft: '18px' }}>
                  {selected.documents.map((doc, idx) => (
                    <li key={idx}>
                      <a
                        href={`/api/edital-challenges/${selected.id}/documents/${idx}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {doc.name || `Documento ${idx + 1}`}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div style={{ marginBottom: '12px' }}>
              <label style={styles.label}>Decisão</label>
              <select value={decision} onChange={(e) => setDecision(e.target.value)} style={styles.input}>
                <option value="deferred">Deferir</option>
                <option value="rejected">Indeferir</option>
              </select>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={styles.label}>Resposta ao interessado</label>
              <textarea
                value={responseText}
                onChange={(e) => setResponseText(e.target.value)}
                rows={5}
                style={{ ...styles.input, resize: 'vertical' }}
                placeholder="Texto da decisão / fundamentação"
              />
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setSelected(null)} disabled={saving} style={getButtonStyle('secondary')}>
                Cancelar
              </button>
              <button type="button" onClick={handleRespond} disabled={saving} style={getButtonStyle('primary')}>
                {saving ? 'Salvando…' : 'Salvar resposta'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
});
